export class HttpError extends Error {
  status: number;
  data: unknown;

  constructor(response: Response, data: unknown) {
    const message =
      typeof data === "object" &&
      data !== null &&
      "message" in data &&
      typeof data.message === "string"
        ? data.message
        : `Request failed with status ${response.status}`;

    super(message);
    this.name = "HttpError";
    this.status = response.status;
    this.data = data;
  }
}

export function isHttpError(error: unknown): error is HttpError {
  return error instanceof HttpError;
}

export function isAuthRecoveryData(data: unknown): boolean {
  return (
    typeof data === "object" &&
    data !== null &&
    "error" in data &&
    // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
    (data as { error?: unknown }).error === "auth_recovery_required"
  );
}

/**
 * True for BFF auth-recovery 401s. Matches both HttpError (dashboard http-client)
 * and CoordinatorHttpError (ui-kit) since both carry `status` and `data`.
 * Callers should suppress competing error UI.
 */
export function isAuthRecoveryHttpError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }
  // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
  const e = error as { status?: unknown; data?: unknown };
  return e.status === 401 && isAuthRecoveryData(e.data);
}

function readStringField(
  obj: Record<string, unknown> | null,
  key: string
): string | undefined {
  const value = obj?.[key];
  return typeof value === "string" ? value : undefined;
}

export function getHttpErrorFields(error: HttpError): {
  name: string;
  message: string;
} {
  const d = error.data;
  const obj =
    // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
    typeof d === "object" && d !== null ? (d as Record<string, unknown>) : null;
  // The Coordinator serializes errors as `{ code, msg, data }` while the BFF and
  // this http-client use `{ message }`. Read `message` first, then the
  // Coordinator's `msg`, so its validation detail (e.g. an OIDC issuer mismatch)
  // surfaces instead of the generic "Request failed with status N" fallback.
  return {
    name: readStringField(obj, "name") ?? error.name,
    message:
      readStringField(obj, "message") ??
      readStringField(obj, "msg") ??
      error.message,
  };
}
